import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { ArrowLeft, Loader2, Mail, RefreshCw, ShoppingCart, Sparkles, TrendingUp, Send } from "lucide-react";

const fmtChf = (n) => `CHF ${Math.round(n || 0).toLocaleString("de-CH")}`;

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("de-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return s; }
};

const ageHours = (s) => {
  if (!s) return 0;
  return Math.round((Date.now() - new Date(s).getTime()) / 3600000);
};

export default function CheckoutRecovery() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState(null);
  const [minAge, setMinAge] = useState(24);
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
  }, [user, loading, navigate]);

  const load = async () => {
    setItems(null);
    const [s, a] = await Promise.all([
      api.get(`/admin/checkout-recovery/stats`),
      api.get(`/admin/checkout-recovery/abandoned?min_age_hours=${minAge}`),
    ]);
    setStats(s.data);
    setItems(a.data);
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, minAge]);

  const sendOne = async (txId) => {
    if (!window.confirm("Recovery-Mail jetzt an diesen Cart senden?")) return;
    setBusy((b) => ({ ...b, [txId]: true }));
    try {
      await api.post(`/admin/checkout-recovery/send/${txId}`, {});
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setBusy((b) => ({ ...b, [txId]: false }));
    }
  };

  const sendBulk = async (dryRun = false) => {
    const msg = dryRun ? "Trockenlauf — keine Mails werden gesendet. Fortfahren?" : `Echte Recovery-Mails an ALLE noch nicht angeschriebenen abgebrochenen Carts älter als ${minAge}h senden?`;
    if (!window.confirm(msg)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/admin/checkout-recovery/send?min_age_hours=${minAge}&dry_run=${dryRun}`);
      const d = r.data;
      if (dryRun) {
        alert(`Trockenlauf: ${d.dry_targets?.length || 0} Carts würden angeschrieben.`);
      } else {
        alert(`Versendet: ${d.sent}\nFehler: ${d.errors?.length || 0}`);
        await load();
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Bulk-Versand fehlgeschlagen");
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;
  if (user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#081328] text-[#F7F6F2] pt-24" data-testid="checkout-recovery-page">
      <div className="container-sat max-w-7xl py-10">
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/55 hover:text-[#D4AF37] text-sm" data-testid="back-to-admin">
            <ArrowLeft size={16} /> Zurück zum Admin-Dashboard
          </Link>
          <button onClick={load} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#D4AF37]" data-testid="reload-recovery">
            <RefreshCw size={14} /> Neu laden
          </button>
        </div>

        <div className="mb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">Checkout-Recovery · Schatten-Arbeit-Bonus</div>
          <h1 className="serif text-4xl md:text-5xl font-light tracking-tight">Verlorene Carts zurückholen.</h1>
          <p className="text-white/55 mt-3 max-w-2xl">Premium-Recovery-Drip mit 48h-Bonus &bdquo;Schatten-Arbeit ohne Drama&ldquo; (Wert CHF 290). Ein Worker prüft stündlich automatisch nach 24h Inaktivität.</p>
        </div>

        {/* KPI Strip */}
        <div className="grid md:grid-cols-4 gap-4 mb-10" data-testid="recovery-kpis">
          <KpiCard icon={<ShoppingCart size={18} />} label="Offen (>24h)" value={stats?.abandoned_24h_with_email ?? "—"} accent="#D4AF37" />
          <KpiCard icon={<Mail size={18} />} label="Mails versendet" value={stats?.recovery_emails_sent ?? "—"} accent="#a48cff" />
          <KpiCard icon={<Sparkles size={18} />} label="Recovered" value={stats?.recovered_after_email ?? "—"} accent="#48d597" />
          <KpiCard icon={<TrendingUp size={18} />} label="Recovery-Rate" value={`${stats?.recovery_rate_pct ?? 0}%`} accent="#F2D6D6" />
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <label className="text-sm text-white/70">Alter (Stunden):</label>
          <input
            type="number" min={0} max={720} value={minAge}
            onChange={(e) => setMinAge(parseInt(e.target.value || "0", 10))}
            className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded text-sm"
            data-testid="min-age-input"
          />
          <button
            onClick={() => sendBulk(true)} disabled={bulkBusy}
            className="px-4 py-2 text-sm border border-white/15 hover:border-[#a48cff] rounded transition disabled:opacity-50"
            data-testid="bulk-dry-run"
          >
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : "Trockenlauf"}
          </button>
          <button
            onClick={() => sendBulk(false)} disabled={bulkBusy}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm bg-[#D4AF37] text-[#081328] font-semibold rounded-full hover:bg-[#e3c252] transition disabled:opacity-50"
            data-testid="bulk-send"
          >
            {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Recovery-Mails senden
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-lg overflow-hidden" data-testid="abandoned-table">
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-white/5 text-xs uppercase tracking-wider text-white/55">
            <div className="col-span-3">E-Mail</div>
            <div className="col-span-3">Produkt</div>
            <div className="col-span-1 text-right">Wert</div>
            <div className="col-span-2">Erstellt</div>
            <div className="col-span-1 text-right">Alter</div>
            <div className="col-span-2 text-right">Aktion</div>
          </div>
          {items === null && (
            <div className="text-center py-16 text-white/55"><Loader2 className="animate-spin mx-auto" /></div>
          )}
          {items?.length === 0 && (
            <div className="text-center py-16 text-white/45">Keine offenen Carts in diesem Zeitfenster.</div>
          )}
          {items?.map((tx) => {
            const sent = !!tx.recovery_sent_at;
            const paid = tx.payment_status === "paid";
            return (
              <div key={tx.id} className="grid grid-cols-12 gap-3 px-5 py-4 border-t border-white/5 items-center text-sm" data-testid={`tx-row-${tx.id}`}>
                <div className="col-span-3 truncate">
                  <div className="font-medium">{tx.first_name || (tx.metadata?.first_name) || "—"}</div>
                  <div className="text-white/55 text-xs">{tx.user_email}</div>
                </div>
                <div className="col-span-3 text-white/75 text-xs">{tx.product_key}</div>
                <div className="col-span-1 text-right text-[#D4AF37]">{fmtChf(tx.amount)}</div>
                <div className="col-span-2 text-white/55 text-xs">{fmtDateTime(tx.created_at)}</div>
                <div className="col-span-1 text-right text-white/55">{ageHours(tx.created_at)}h</div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  {paid && <span className="text-[10px] uppercase tracking-wider text-[#48d597] border border-[#48d597]/40 px-2 py-0.5 rounded">Bezahlt</span>}
                  {sent && <span className="text-[10px] uppercase tracking-wider text-[#a48cff] border border-[#a48cff]/40 px-2 py-0.5 rounded">Mail gesendet</span>}
                  {!sent && !paid && (
                    <button
                      onClick={() => sendOne(tx.id)} disabled={!!busy[tx.id]}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#081328] rounded-full transition disabled:opacity-50"
                      data-testid={`send-recovery-${tx.id}`}
                    >
                      {busy[tx.id] ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                      Senden
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const KpiCard = ({ icon, label, value, accent }) => (
  <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">{label}</div>
      <span style={{ color: accent }}>{icon}</span>
    </div>
    <div className="serif text-3xl font-light" style={{ color: accent }}>{value}</div>
  </div>
);
